import Phaser from 'phaser';

import { Subscription } from 'rxjs';
import { SolitaireMainScene } from '../../scenes/solitaire-main-scene';
import { CardViewModel } from './card-view-model';
import { CardFrontView } from './card-front-view';
import { CardBackView } from './card-back-view';
import { CardShadowView } from './card-shadow-view';
import { DragData } from '../board/board-view-model';
import { ResizeMetrics } from '../../resize-metrics';

export class CardView extends Phaser.GameObjects.Container {
  private currentResizeMetrics?: ResizeMetrics;
  private subscriptionGroup: Subscription;
  private viewModel: CardViewModel;
  private clickRectangle: Phaser.GameObjects.Rectangle;
  private cardShadowView: CardShadowView;
  private cardBackView: CardBackView;
  private cardFrontView: CardFrontView;
  private flipTween?: Phaser.Tweens.Tween;
  private shakeTween?: Phaser.Tweens.Tween;

  private shakeValue = 0;
  private isShaking = false;
  private flipValue = 0;
  private isShowingFace = false;
  private targetPosX = 0;
  private targetPosY = 0;
  private hasDragged = false;
  private pointerWasDown = false;
  private dragStartPos = new Phaser.Geom.Point();
  private dragMaxDistance = 0;
  private stackDragOffsetX = 0;
  private stackDragOffsetY = 0;

  constructor(scene: SolitaireMainScene, x: number, y: number, viewModel: CardViewModel) {
    super(scene, x, y);

    this.viewModel = viewModel;
    this.subscriptionGroup = new Subscription();

    this.cardShadowView = new CardShadowView(scene, 0, 0, viewModel);
    this.cardBackView = new CardBackView(scene, 0, 0, viewModel);
    this.cardFrontView = new CardFrontView(scene, 0, 0, viewModel);
    this.cardFrontView.setVisible(false);

    this.add([this.cardShadowView, this.cardBackView, this.cardFrontView]);

    this.clickRectangle = scene.add
      .rectangle(0, 0, 0, 0, 0x000000)
      .setAlpha(0.0)
      .setOrigin(0.5, 0.5);
    this.add(this.clickRectangle);

    this.setName(CardView.name);
    scene.add.existing(this);

    this.handleInput();

    this.subscriptionGroup.add(
      this.viewModel.onShakeTrigger.subscribe((_value) => {
        this.shakeCard();
      }),
    );

    this.subscriptionGroup.add(
      this.viewModel.currentDepth.subscribe((value) => {
        this.depth = value;
      }),
    );
  }

  override destroy(_fromScene?: boolean | undefined): void {
    if (this.flipTween) {
      this.flipTween.stop();
      this.scene.tweens.remove(this.flipTween);
    }

    if (this.shakeTween) {
      this.shakeTween.stop();
      this.scene.tweens.remove(this.shakeTween);
    }

    this.subscriptionGroup.unsubscribe();

    super.destroy(_fromScene);
  }

  public sceneUpdate(_time: number, delta: number) {
    if (!this.currentResizeMetrics) {
      return;
    }

    this.cardShadowView.refreshInterface(delta);
    this.cardFrontView.refreshInterface();

    if (this.viewModel.isDragging || this.viewModel.isStackDragging || this.isShaking) {
      return;
    }

    if (this.viewModel.canFlipCard()) {
      this.flipCard(this.viewModel.isFaceUp);
    }

    const calcPos = this.viewModel.calculateTargetPosition(this.currentResizeMetrics);
    this.targetPosX = calcPos.x;
    this.targetPosY = calcPos.y;

    this.viewModel.setMovement(
      Phaser.Math.Distance.Between(this.x, this.y, this.targetPosX, this.targetPosY),
    );

    const speed = 0.1;
    const targetDelta = 0.12 * delta * speed;

    this.setPosition(
      this.x * (1 - targetDelta) + this.targetPosX * targetDelta,
      this.y * (1 - targetDelta) + this.targetPosY * targetDelta,
    );
  }

  public onResize(resizeMetrics: ResizeMetrics) {
    this.currentResizeMetrics = resizeMetrics;

    const cardScaleFactor = resizeMetrics.scalingMetrics.scaleFactor;

    const gameAsset = this.viewModel.cardBackAsset;
    const displayWidth = gameAsset.dimensions.width * cardScaleFactor;
    const displayHeight = gameAsset.dimensions.height * cardScaleFactor;

    const calcPos = this.viewModel.calculateTargetPosition(resizeMetrics);
    this.targetPosX = calcPos.x;
    this.targetPosY = calcPos.y;
    this.setPosition(this.targetPosX, this.targetPosY);

    this.setSize(displayWidth, displayHeight);
    this.clickRectangle.setSize(displayWidth, displayHeight);

    this.setInteractive({
      hitArea: this.clickRectangle,
      draggable: true,
      hitAreaCallback: (rect: Phaser.Geom.Rectangle, x: number, y: number) =>
        Phaser.Geom.Rectangle.Contains(rect, x, y),
    });

    this.cardShadowView.onResize(resizeMetrics);
    this.cardBackView.onResize(resizeMetrics);
    this.cardFrontView.onResize(resizeMetrics);
  }

  public handleCardDragStart(dragData: DragData) {
    if (this.viewModel.canDragInStack(dragData)) {
      this.viewModel.setStackDragging(true);
      this.stackDragOffsetX = this.x - dragData.dragX;
      this.stackDragOffsetY = this.y - dragData.dragY;
    }
  }

  public handleCardDrag(dragData: DragData) {
    if (this.viewModel.isStackDragging) {
      this.x = dragData.dragX + this.stackDragOffsetX;
      this.y = dragData.dragY + this.stackDragOffsetY;
    }

    // Is the dragging card on top of this card?
    const rect = this.cardFrontView.getBounds();
    if (rect.contains(dragData.dragX, dragData.dragY)) {
      this.viewModel.draggingOver(dragData.cardViewModel);
    }
  }

  public handleCardDragEnd(dragData: DragData) {
    if (this.viewModel.isStackDragging) {
      this.viewModel.setStackDragging(false);
    }

    // Is the dragging card on top of this card?
    const rect = this.cardFrontView.getBounds();
    if (rect.contains(dragData.dragX, dragData.dragY)) {
      this.viewModel.droppedOver(dragData.cardViewModel);
    }
  }

  private flipCard(isFaceUp: boolean) {
    if (this.flipTween !== undefined && this.flipTween.isPlaying()) {
      this.flipTween.stop();
      this.flipValue = 0;
      this.scaleX = 1;
    }

    const flipDuration = this.viewModel.isInWaste ? 140 : 115;

    this.flipTween = this.scene.tweens.add({
      targets: this,
      flipValue: 1,
      duration: flipDuration,
      yoyo: true,
      ease: Phaser.Math.Easing.Quadratic.In,
      onYoyo: (_tween, _target, isYoyo) => {
        if (isYoyo) {
          if (this.isShowingFace !== isFaceUp) {
            this.cardBackView.visible = !isFaceUp;
            this.cardFrontView.visible = isFaceUp;
            this.isShowingFace = isFaceUp;
          }
        }
      },
      onUpdate: (_tween) => {
        this.scaleX = 1 - this.flipValue;
      },
      onComplete: () => {
        this.flipValue = 0;
        this.scaleX = 1;
      },
    });
  }

  private shakeCard() {
    if (this.shakeTween !== undefined && this.shakeTween.isPlaying()) {
      return;
    }

    function wiggle(progress: number, period1: number, period2: number): number {
      const current1 = progress * Math.PI * 2 * period1;
      const current2 = progress * Math.PI * 2 * period2;

      return Math.sin(current1) * Math.cos(current2);
    }

    const dpr = window.devicePixelRatio;

    this.shakeTween = this.scene.tweens.add({
      targets: this,
      duration: 150,
      shakeValue: 1,
      onStart: () => {
        this.isShaking = true;
      },
      onUpdate: (tween) => {
        this.x += wiggle(tween.progress, 7, 3) * 5 * dpr;
        this.y += wiggle(tween.progress, 8, 4) * 5 * dpr;
      },
      onComplete: () => {
        this.isShaking = false;
        this.shakeValue = 0;
      },
    });
  }

  private handleInput() {
    this.on(
      Phaser.Input.Events.DRAG_START,
      (
        _pointer: Phaser.Input.Pointer,
        _dragX: number,
        _dragY: number,
        _gameObject: Phaser.GameObjects.Image,
      ) => {
        this.dragStartPos.setTo(this.x, this.y);

        if (this.viewModel.canDrag) {
          this.viewModel.dragStart(this.x, this.y);
        }
      },
    )
      .on(
        Phaser.Input.Events.DRAG,
        (_pointer: Phaser.Input.Pointer, dragX: number, dragY: number) => {
          this.dragMaxDistance = Math.max(
            this.dragMaxDistance,
            Phaser.Math.Distance.Between(this.x, this.y, this.dragStartPos.x, this.dragStartPos.y),
          );
          this.hasDragged = true;

          if (!this.viewModel.isDragging) {
            return;
          }

          this.x = dragX;
          this.y = dragY;
          this.viewModel.drag(dragX, dragY);
        },
      )
      .on(
        Phaser.Input.Events.DRAG_END,
        (
          _pointer: Phaser.Input.Pointer,
          _dragX: number,
          _dragY: number,
          _gameObject: Phaser.GameObjects.Image,
        ) => {
          this.viewModel.dragEnd(this.x, this.y);
        },
      )
      .on(Phaser.Input.Events.POINTER_DOWN, (_pointer: Phaser.Input.Pointer) => {
        this.hasDragged = false;
        this.pointerWasDown = true;
      })
      .on(Phaser.Input.Events.POINTER_UP, (_pointer: Phaser.Input.Pointer) => {
        if (
          (!this.hasDragged || this.dragMaxDistance < CardViewModel.dragDistanceThreshold) &&
          this.pointerWasDown
        ) {
          this.viewModel.performClick();
        }
        this.pointerWasDown = false;
        this.hasDragged = false;
        this.dragMaxDistance = 0;
      });
  }
}
